import { useState, useCallback, useEffect, useRef, useMemo } from 'react'

import { Editor } from './components/Editor'
import { Toolbar } from './components/Toolbar'
import { StatusBar } from './components/StatusBar'
import { CommandPalette } from './components/CommandPalette'
import { AuthScreen } from './components/AuthScreen'
import { SettingsScreen } from './components/SettingsScreen'

import { useNotes, createNote, updateNote, updateNoteTitle, getNote, deleteNote, deleteAllNotes, getDisplayTitle } from './hooks/useNotes'
import { db } from './db/dexie'
import { useTheme } from './hooks/useTheme'
import { useAuth } from './hooks/useAuth'
import { useSync } from './hooks/useSync'
import { track, AnalyticsEvents } from './lib/mixpanel'
import { captureError } from './lib/errorReporting'

function App() {
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null)
  const [editorContent, setEditorContent] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [isPaletteOpen, setIsPaletteOpen] = useState(false)
  const [showAuthScreen, setShowAuthScreen] = useState(false)
  const [authScreenError, setAuthScreenError] = useState<string | null>(null)
  const [showSettingsScreen, setShowSettingsScreen] = useState(false)
  const [pendingPostAuthSync, setPendingPostAuthSync] = useState(false)
  const [checkoutVerifying, setCheckoutVerifying] = useState(false)
  const [checkoutVerified, setCheckoutVerified] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { theme, toggleTheme } = useTheme()

  // Capture Stripe checkout params once at mount, before any replaceState clears them
  const [checkoutSessionId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout_success') === 'true') {
      // Clean up the URL immediately so it doesn't linger in the address bar
      window.history.replaceState(null, '', window.location.pathname)
      return params.get('session_id')
    }
    return null
  })

  const notes = useNotes()
  const isCreatingInitialNoteRef = useRef(false)
  const isCreatingNoteRef = useRef(false)

  // Auth
  const {
    user,
    session,
    loading: authLoading,
    isPro,
    isAuthenticated,
    isReturningFromOAuth,
    oauthCallbackError,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    updatePassword,
    refreshProfile,
    verifyCheckoutAndRefreshProfile,
  } = useAuth()

  // Sync — enabled for anyone who is signed in
  const { status: syncStatus, sync: performSync, debouncedSync } = useSync({
    userId: user?.id ?? null,
    enabled: isAuthenticated,
    accessToken: session?.access_token ?? null,
  })

  // Track the custom title for the current note
  const [customTitle, setCustomTitle] = useState<string | null>(null)

  // Get current note title — memoized to avoid regex parsing on every render
  const currentNoteTitle = useMemo(
    () => currentNoteId && (editorContent || customTitle)
      ? getDisplayTitle({ content: editorContent, created_at: new Date().toISOString(), custom_title: customTitle })
      : 'Untitled',
    [currentNoteId, editorContent, customTitle]
  )

  // Load note content and custom title when switching notes
  useEffect(() => {
    if (currentNoteId) {
      getNote(currentNoteId).then(note => {
        if (note) {
          setEditorContent(note.content)
          setCustomTitle(note.custom_title ?? null)
        }
      }).catch(err => captureError(err, { source: 'app.loadNote', extra: { noteId: currentNoteId } }))
    }
  }, [currentNoteId])

  // Discard empty notes when navigating away
  const discardIfEmpty = useCallback(async (noteId: string | null) => {
    if (!noteId) return
    const note = await getNote(noteId)
    if (note) {
      const plainText = note.content.replace(/<[^>]*>/g, '').trim()
      if (!plainText) {
        await deleteNote(noteId)
        debouncedSync(noteId)
        track(AnalyticsEvents.NOTE_DELETED, { reason: 'empty_discard' })
      }
    }
  }, [debouncedSync])

  // Handle title change from toolbar
  const handleTitleChange = useCallback(async (title: string | null) => {
    if (!currentNoteId) return
    setCustomTitle(title)
    await updateNoteTitle(currentNoteId, title)
    debouncedSync(currentNoteId)
  }, [currentNoteId, debouncedSync])

  // Create a new note and switch to it
  const handleNewNote = useCallback(async (source: string = 'toolbar_button') => {
    // Guard against double invocation (rapid clicks / keyboard repeats)
    if (isCreatingNoteRef.current) return
    isCreatingNoteRef.current = true
    try {
      await discardIfEmpty(currentNoteId)
      const note = await createNote()
      setCurrentNoteId(note.id)
      setEditorContent('')
      setCustomTitle(null)
      setWordCount(0)
      setIsPaletteOpen(false)
      track(AnalyticsEvents.NOTE_CREATED, { source })
    } finally {
      isCreatingNoteRef.current = false
    }
  }, [discardIfEmpty, currentNoteId])

  // Delete a single note from command palette
  const handleDeleteNote = useCallback(async (noteId: string) => {
    await deleteNote(noteId)
    debouncedSync(noteId)
    track(AnalyticsEvents.NOTE_DELETED, { source: 'command_palette' })
    // If we just deleted the current note, switch to another
    if (noteId === currentNoteId) {
      const remaining = notes.filter(n => n.id !== noteId)
      if (remaining.length > 0) {
        setCurrentNoteId(remaining[0].id)
        setEditorContent(remaining[0].content)
        setCustomTitle(remaining[0].custom_title ?? null)
      } else {
        // No notes left — create a fresh one
        const fresh = await createNote()
        setCurrentNoteId(fresh.id)
        setEditorContent('')
        setCustomTitle(null)
        setWordCount(0)
      }
    }
  }, [currentNoteId, notes, debouncedSync])

  // Delete all notes
  const handleDeleteAllNotes = useCallback(async () => {
    await deleteAllNotes(user?.id, isAuthenticated)
    track(AnalyticsEvents.ALL_NOTES_DELETED)
    // Create a fresh empty note
    const fresh = await createNote()
    setCurrentNoteId(fresh.id)
    setEditorContent('')
    setCustomTitle(null)
    setWordCount(0)
  }, [user?.id, isAuthenticated])

  // Open command palette
  const handleOpenPalette = useCallback((source: string = 'toolbar_button') => {
    setIsPaletteOpen(true)
    track(AnalyticsEvents.COMMAND_PALETTE_OPENED, { source })
  }, [])

  // Select a note from search
  const handleSelectNote = useCallback(async (noteId: string) => {
    await discardIfEmpty(currentNoteId)
    const note = await getNote(noteId)
    if (note) {
      setEditorContent(note.content)
      setCustomTitle(note.custom_title ?? null)
    }
    setCurrentNoteId(noteId)
    track(AnalyticsEvents.NOTE_OPENED, { source: 'command_palette' })
  }, [discardIfEmpty, currentNoteId])

  // Handle content changes — save to IndexedDB immediately, debounce push
  const handleContentChange = useCallback(
    (content: string) => {
      setEditorContent(content)

      if (!currentNoteId) return

      // Show saving indicator
      setSaveStatus('saving')
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)

      // Save to IndexedDB immediately — no debounce. This ensures every
      // keystroke is persisted locally so nothing is lost on tab close.
      updateNote(currentNoteId, content)

      // Mark as saved after a brief moment — stays persistent
      saveTimerRef.current = setTimeout(() => {
        setSaveStatus('saved')
      }, 300)

      // Always request a push — the sync layer decides what's worth sending,
      // which lets clearing a note reach the server instead of being dropped
      // here as "blank".
      debouncedSync(currentNoteId)
    },
    [currentNoteId, debouncedSync]
  )

  // Handle word count changes
  const handleWordCountChange = useCallback((count: number) => {
    setWordCount(count)
  }, [])

  // Handle sync button click
  const handleSync = useCallback(() => {
    // Don't show auth screen while session is still loading
    if (authLoading) return
    if (!isAuthenticated) {
      setShowAuthScreen(true)
      return
    }
    track(AnalyticsEvents.SYNC_TRIGGERED, { source: 'toolbar_button' })
    performSync()
  }, [authLoading, isAuthenticated, performSync])

  // Toolbar theme toggle
  const handleToggleThemeToolbar = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    toggleTheme()
    track(AnalyticsEvents.THEME_TOGGLED, { to_theme: newTheme, source: 'toolbar_button' })
  }, [theme, toggleTheme])

  // Palette theme toggle
  const handleToggleThemePalette = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    toggleTheme()
    track(AnalyticsEvents.THEME_TOGGLED, { to_theme: newTheme, source: 'command_palette' })
  }, [theme, toggleTheme])

  // Sign out
  const handleSignOut = useCallback(() => {
    track(AnalyticsEvents.SIGN_OUT)
    signOut()
  }, [signOut])

  // Settings
  const handleSettings = useCallback(() => {
    track(AnalyticsEvents.SETTINGS_OPENED)
    setShowSettingsScreen(true)
  }, [])

  // Close palette
  const handleClosePalette = useCallback(() => {
    setIsPaletteOpen(false)
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('.ProseMirror')?.focus()
    })
  }, [])

  // New note from palette
  const handleNewNotePalette = useCallback(() => handleNewNote('command_palette'), [handleNewNote])


  const handleSignIn = useCallback(async (email: string, password: string) => {
    track(AnalyticsEvents.SIGN_IN_STARTED, { method: 'email' })
    await signInWithEmail(email, password)
    track(AnalyticsEvents.SIGN_IN_COMPLETED, { method: 'email' })
    setShowAuthScreen(false)
    setPendingPostAuthSync(true)
  }, [signInWithEmail])

  const handleSignUp = useCallback(async (email: string, password: string) => {
    track(AnalyticsEvents.SIGN_IN_STARTED, { method: 'email' })
    await signUpWithEmail(email, password)
    track(AnalyticsEvents.SIGN_UP_COMPLETED, { method: 'email' })
    setShowAuthScreen(false)
    setPendingPostAuthSync(true)
  }, [signUpWithEmail])

  const handleGoogleSignIn = useCallback(async () => {
    track(AnalyticsEvents.SIGN_IN_STARTED, { method: 'google' })
    await signInWithGoogle()
    // OAuth redirects the browser; onAuthStateChange handles the session on return
  }, [signInWithGoogle])

  // After returning from OAuth sign-in, sync immediately
  useEffect(() => {
    if (isReturningFromOAuth && isAuthenticated && !authLoading) {
      performSync()
    }
  }, [isReturningFromOAuth, isAuthenticated, authLoading, performSync])

  // If Google/Supabase bounced us back with an error, reopen the auth screen
  // with the message. Previously the URL was stripped and the editor loaded
  // as if nothing happened, so login looked "broken" with no explanation.
  useEffect(() => {
    if (!oauthCallbackError || authLoading || isAuthenticated) return
    captureError(new Error(oauthCallbackError), { source: 'auth.oauthCallback' })
    setAuthScreenError(oauthCallbackError)
    setShowAuthScreen(true)
  }, [oauthCallbackError, authLoading, isAuthenticated])

  // After email sign-in/sign-up, sync immediately
  useEffect(() => {
    if (pendingPostAuthSync && isAuthenticated && !authLoading) {
      setPendingPostAuthSync(false)
      performSync()
    }
  }, [pendingPostAuthSync, isAuthenticated, authLoading, performSync])

  // Handle return from Stripe checkout — the session ID was captured at mount
  // in the checkoutSessionId state initializer. We wait until auth has loaded
  // and the user is available before verifying, because verifyCheckoutAndRefreshProfile
  // requires state.user to be set.
  useEffect(() => {
    if (checkoutSessionId === null) return
    if (authLoading || !isAuthenticated) return

    setCheckoutVerifying(true)

    // Verify the checkout session server-side and update Pro status
    verifyCheckoutAndRefreshProfile(checkoutSessionId)
      .then(() => {
        setCheckoutVerifying(false)
        setCheckoutVerified(true)
        track(AnalyticsEvents.SUBSCRIPTION_COMPLETED, { source: 'stripe_checkout' })
        // Auto-dismiss the success screen after 2.5 seconds
        setTimeout(() => setCheckoutVerified(false), 2500)
      })
      .catch(() => {
        setCheckoutVerifying(false)
      })
  }, [checkoutSessionId, authLoading, isAuthenticated, verifyCheckoutAndRefreshProfile])

  // Register keyboard shortcut: Cmd+K only
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey

      if (isMod && e.key === 'k') {
        e.preventDefault()
        setIsPaletteOpen(prev => {
          if (!prev) {
            track(AnalyticsEvents.COMMAND_PALETTE_OPENED, { source: 'keyboard_shortcut' })
          }
          return !prev
        })
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Create initial note if none exists
  useEffect(() => {
    if (currentNoteId !== null) return

    if (notes.length > 0) {
      setCurrentNoteId(notes[0].id)
      setEditorContent(notes[0].content)
      setCustomTitle(notes[0].custom_title ?? null)
      track(AnalyticsEvents.NOTE_OPENED, { source: 'auto' })
      return
    }

    // Guard against double invocation (React StrictMode runs effects twice)
    if (isCreatingInitialNoteRef.current) return
    isCreatingInitialNoteRef.current = true

    createNote().then(note => {
      setCurrentNoteId(note.id)
      setEditorContent('')
      setWordCount(0)
      isCreatingInitialNoteRef.current = false
      track(AnalyticsEvents.NOTE_CREATED, { source: 'initial' })
    }).catch(err => {
      isCreatingInitialNoteRef.current = false
      captureError(err, { source: 'app.createInitialNote' })
    })
  }, [currentNoteId, notes])

  // No need for visibilitychange flush — IndexedDB saves are immediate

  // Clean up stale blank notes on app startup
  useEffect(() => {
    db.notes
      .filter(note => !note.is_deleted)
      .toArray()
      .then(allNotes => {
        const blankNotes = allNotes.filter(n => {
          const plainText = n.content.replace(/<[^>]*>/g, '').trim()
          return !plainText
        })
        // Keep the most recent blank note (it may be the active one) and discard the rest
        if (blankNotes.length > 1) {
          const sorted = blankNotes.sort(
            (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )
          // Delete all but the most recent
          sorted.slice(1).forEach(n => deleteNote(n.id))
        }
      })
      .catch(err => captureError(err, { source: 'app.cleanupBlankNotes' }))
  }, [])


  // Show loading screen while processing OAuth callback (returning from Google sign-in)
  if (authLoading && isReturningFromOAuth && !oauthCallbackError) {
    return (
      <div className="h-screen flex items-center justify-center bg-yellow-50 dark:bg-neutral-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-4" />
          <p className="text-neutral-600 dark:text-neutral-400">Signing you in...</p>
        </div>
      </div>
    )
  }

  // Show loading screen while verifying Stripe checkout
  if (checkoutVerifying) {
    return (
      <div className="h-screen flex items-center justify-center bg-yellow-50 dark:bg-neutral-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-4" />
          <p className="text-neutral-600 dark:text-neutral-400">Activating your Pro subscription...</p>
        </div>
      </div>
    )
  }

  // Show success screen after checkout verification
  if (checkoutVerified) {
    return (
      <div className="h-screen flex items-center justify-center bg-yellow-50 dark:bg-neutral-900">
        <div className="text-center animate-fade-in">
          <div className="text-4xl mb-4">🎉</div>
          <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100 mb-2">Welcome to Pro!</h2>
          <p className="text-neutral-600 dark:text-neutral-400">You now have access to sync and all Pro features.</p>
        </div>
      </div>
    )
  }

  // Show auth screen when sync clicked and not authenticated
  if (showAuthScreen) {
    return (
      <AuthScreen
        onBack={() => {
          setShowAuthScreen(false)
          setAuthScreenError(null)
        }}
        onSignIn={handleSignIn}
        onSignUp={handleSignUp}
        onGoogleSignIn={handleGoogleSignIn}
        theme={theme}
        initialError={authScreenError}
      />
    )
  }

  // Show settings screen
  if (showSettingsScreen) {
    return (
      <SettingsScreen
        isPro={isPro}
        isAuthenticated={isAuthenticated}
        onBack={() => setShowSettingsScreen(false)}
        onUpdatePassword={updatePassword}
        onRefreshProfile={refreshProfile}
        onDeleteAllNotes={handleDeleteAllNotes}
      />
    )
  }

  return (
    <div className="app-container">
      <Toolbar
        noteTitle={currentNoteTitle}
        customTitle={customTitle}
        onTitleChange={handleTitleChange}
        onNewNote={handleNewNote}
        onOpenPalette={handleOpenPalette}
        onSync={handleSync}
        isAuthenticated={isAuthenticated}
        authLoading={authLoading}
        theme={theme}
        onToggleTheme={handleToggleThemeToolbar}
        userEmail={user?.email ?? null}
        onSignOut={handleSignOut}
        onSettings={handleSettings}
      />

      <div className="main-content">
        {/* Left spacing */}
        <aside className="side-panel" />

        {/* Center Editor Frame */}
        <main className="editor-frame">
          <Editor
            noteId={currentNoteId}
            content={editorContent}
            onChange={handleContentChange}
            onWordCountChange={handleWordCountChange}
          />

          <StatusBar
            noteCount={notes.length}
            wordCount={wordCount}
            saveStatus={saveStatus}
            syncStatus={syncStatus}
            isAuthenticated={isAuthenticated}
          />
        </main>

        {/* Right spacing */}
        <aside className="side-panel" />
      </div>

      <CommandPalette
        isOpen={isPaletteOpen}
        onClose={handleClosePalette}
        onSelectNote={handleSelectNote}
        onNewNote={handleNewNotePalette}
        onToggleTheme={handleToggleThemePalette}
        onDeleteNote={handleDeleteNote}
        onPinToggle={debouncedSync}
      />
    </div>
  )
}

export default App
